import type { ReploShopifyProduct } from "schemas/runtimeProduct";

import React from "react";

import { isOptionEnabled as isOptionEnabledUtil } from "../../../utils/option";

interface ProductTileContextType {
  productData: ReploShopifyProduct | undefined;
  selectedVariantId: string | null;
  selectedVariant: ReploShopifyProduct["variants"][0] | null;
  selectedOptions: {
    option1?: string;
    option2?: string;
    option3?: string;
  };
  selectOptionValue: (
    optionKey: "option1" | "option2" | "option3",
    value: string,
  ) => void;
  getProductOptionByKey: (
    optionKey: string,
  ) => ReploShopifyProduct["options"][0] | undefined;
  isOptionEnabled: (
    optionKey: "option1" | "option2" | "option3",
    optionValue: string,
  ) => boolean;
}

const ProductTileContext = React.createContext<ProductTileContextType | null>(
  null,
);

export const useProductTileContext = () => {
  const context = React.useContext(ProductTileContext);
  if (!context) {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(
      "useProductTileContext must be used within a ProductTileProvider",
    );
  }
  return context;
};

type ProductTileProviderProps = React.PropsWithChildren<{
  productData: ReploShopifyProduct | undefined;
  selectedVariantId: string | null;
  onVariantChange: (variantId: string) => void;
}>;

export const ProductTileProvider: React.FC<ProductTileProviderProps> = ({
  productData,
  selectedVariantId,
  onVariantChange,
  children,
}) => {
  const selectedVariant = React.useMemo(() => {
    if (!productData || !selectedVariantId) {
      return null;
    }
    return (
      productData.variants.find(
        (v) => String(v.id) === String(selectedVariantId),
      ) ?? null
    );
  }, [productData, selectedVariantId]);

  // Track partial option selections
  const selectedOptions = React.useMemo(() => {
    if (!selectedVariant) {
      return {};
    }
    return {
      option1: selectedVariant.option1,
      option2: selectedVariant.option2 ?? undefined,
      option3: selectedVariant.option3 ?? undefined,
    };
  }, [selectedVariant]);

  const selectOptionValue = React.useCallback(
    (optionKey: "option1" | "option2" | "option3", value: string) => {
      if (!productData || !selectedVariant) {
        return;
      }

      // Define option hierarchy
      const optionHierarchy = ["option1", "option2", "option3"];
      const currentOptionIndex = optionHierarchy.indexOf(optionKey);

      // Start with current selections
      const newSelectedOptions = {
        option1: selectedVariant.option1,
        option2: selectedVariant.option2,
        option3: selectedVariant.option3,
      };

      // Update the selected option
      newSelectedOptions[optionKey] = value;

      // Clear or reset downstream options that are now invalid
      for (let i = currentOptionIndex + 1; i < optionHierarchy.length; i++) {
        // Legacy cast - may or may not be necessary
        // eslint-disable-next-line replo/no-as-cast
        const downstreamKey = optionHierarchy[i] as
          | "option1"
          | "option2"
          | "option3";
        const currentDownstreamValue = newSelectedOptions[downstreamKey];

        if (currentDownstreamValue) {
          // Check if this downstream option is still valid with the new upstream selection
          const testOptions: Record<string, string> = {};
          for (let j = 0; j <= i; j++) {
            // Legacy cast - may or may not be necessary
            // eslint-disable-next-line replo/no-as-cast
            const testKey = optionHierarchy[j] as
              | "option1"
              | "option2"
              | "option3";
            const testValue = newSelectedOptions[testKey];
            if (testValue) {
              testOptions[testKey] = testValue;
            }
          }

          // Find variants that match this combination
          const matchingVariants = productData.variants.filter((variant) => {
            return Object.entries(testOptions).every(([key, val]) => {
              return (
                // Legacy cast - may or may not be necessary
                // eslint-disable-next-line replo/no-as-cast
                String(variant[key as keyof typeof variant]) === String(val)
              );
            });
          });

          // If no available variants match, find the first available option for this level
          if (!matchingVariants.some((variant) => variant.available)) {
            // Find the first available option for this downstream key
            const downstreamOption = productData.options.find(
              (opt) => opt.key === downstreamKey,
            );
            if (downstreamOption?.values) {
              let foundValidOption = false;
              for (const optionValue of downstreamOption.values) {
                const testWithThisOption = {
                  ...testOptions,
                  [downstreamKey]: optionValue.title,
                };
                const variantsWithThisOption = productData.variants.filter(
                  (variant) => {
                    return Object.entries(testWithThisOption).every(
                      ([key, val]) => {
                        return (
                          // Legacy cast - may or may not be necessary
                          // eslint-disable-next-line replo/no-as-cast
                          String(variant[key as keyof typeof variant]) ===
                          String(val)
                        );
                      },
                    );
                  },
                );
                if (
                  variantsWithThisOption.some((variant) => variant.available)
                ) {
                  newSelectedOptions[downstreamKey] = optionValue.title;
                  foundValidOption = true;
                  break;
                }
              }
              // If no valid option found, use the first option (will be disabled)
              if (!foundValidOption && downstreamOption.values[0]) {
                newSelectedOptions[downstreamKey] =
                  downstreamOption.values[0].title;
              }
            }
          }
        }
      }

      // Find the best matching variant with the new selections
      const newSelectedVariant = productData.variants.find((variant) => {
        return (
          variant.option1 === newSelectedOptions.option1 &&
          variant.option2 === newSelectedOptions.option2 &&
          variant.option3 === newSelectedOptions.option3
        );
      });

      if (newSelectedVariant) {
        onVariantChange(String(newSelectedVariant.id));
      }
    },
    [productData, selectedVariant, onVariantChange],
  );

  const getProductOptionByKey = React.useCallback(
    (optionKey: string) => {
      if (!productData) {
        return undefined;
      }
      return productData.options.find((option) => option.key === optionKey);
    },
    [productData],
  );

  // Check if an option value should be enabled given current partial selections
  // Uses hierarchy: only considers selections from earlier options (option1 before option2, etc.)
  const isOptionEnabled = React.useCallback(
    (optionKey: "option1" | "option2" | "option3", optionValue: string) => {
      if (!productData) {
        return false;
      }
      return isOptionEnabledUtil(
        productData,
        selectedOptions,
        optionKey,
        optionValue,
      );
    },
    [productData, selectedOptions],
  );

  const value = React.useMemo(
    () => ({
      productData,
      selectedVariantId,
      selectedVariant,
      selectedOptions,
      selectOptionValue,
      getProductOptionByKey,
      isOptionEnabled,
    }),
    [
      productData,
      selectedVariantId,
      selectedVariant,
      selectedOptions,
      selectOptionValue,
      getProductOptionByKey,
      isOptionEnabled,
    ],
  );

  return (
    <ProductTileContext.Provider value={value}>
      {children}
    </ProductTileContext.Provider>
  );
};
